import { createElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { SorareLogo } from '#src/atoms/icons/SorareLogo/index.tsx';
import { Container } from '#src/atoms/layout/Container/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { LinkOther } from '#src/atoms/navigation/Box/index.tsx';
import { SmallerStarBall } from '#src/atoms/navigation/SmallerStarBall/index.tsx';
import { ResponsiveSearchBar } from '#src/components/search/ResponsiveSearchBar/index.tsx';
import { LANDING } from '#src/constants/__generated__/routes.ts';
import { useDefaultSportPages } from '#src/constants/routes.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { glossary } from '#src/lib/glossary.ts';
import { getFormattedSport, sportIcons } from '#src/lib/sports.ts';
import {
  breakpoints,
  laptopAndAbove,
  mobileAndAbove,
} from '#src/style/mediaQuery.ts';

const Wrapper = styled(SBHorizontal).attrs({ gap: 0 })`
  z-index: 1;
  position: relative;
  margin: auto;
  width: 100%;
  height: var(--app-header-height-mobile);
`;

const SportsLinks = styled.div`
  display: flex;
  flex: 1;
  gap: var(--unit);
`;

const Logo = styled(Link)`
  display: flex;
  gap: var(--unit);
  align-items: center;
  @media ${mobileAndAbove} {
    margin-right: var(--double-unit);
  }
`;

const SportButton = styled(Link)`
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: var(--unit);
  justify-content: space-between;
  @media ${laptopAndAbove} {
    padding: var(--unit);
    &:hover {
      border-radius: var(--unit);
    }
  }
`;

const MobileSportButtonsWrapper = styled(SBHorizontal)`
  padding: 0 0 var(--unit);
  overflow: auto;
`;

const ConnexionButtons = () => {
  const { signIn, signUp } = useConnectionContext();
  return (
    <Horizontal center>
      <Button size="small" color="primary" onClick={signUp}>
        <FormattedMessage {...glossary.signup} />
      </Button>
      <Button size="small" color="tertiary" onClick={signIn}>
        <FormattedMessage {...glossary.signin} />
      </Button>
    </Horizontal>
  );
};

const SportButtons = () => {
  const defaultSportPages = useDefaultSportPages();

  return [Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL].map(sport => (
    <LinkOther as={SportButton} to={defaultSportPages[sport]} key={sport}>
      {getFormattedSport(sport)}
      {createElement(sportIcons[sport])}
    </LinkOther>
  ));
};

export const LandingAppHeader = () => {
  const { up: isMobile } = useScreenSize('mobile');
  const { up: isLaptop } = useScreenSize('laptop');
  return (
    <Container maxWidth={breakpoints.mediumDesktop}>
      <Vertical gap={0}>
        <Wrapper>
          <SportsLinks>
            <Logo to={LANDING} title="Sorare">
              {isMobile ? <SorareLogo /> : <SmallerStarBall />}
            </Logo>
            {!!isLaptop && <SportButtons />}
          </SportsLinks>
          <ResponsiveSearchBar />
          <ConnexionButtons />
        </Wrapper>
        {!isLaptop && (
          <MobileSportButtonsWrapper gap={2}>
            <SportButtons />
          </MobileSportButtonsWrapper>
        )}
      </Vertical>
    </Container>
  );
};
